import React, { useState } from 'react'
import { FaHeart } from 'react-icons/fa'
import usePersonalOrdersByCustomerId from '../../hooks/usepersonalOrderByCustomerID'
import usePermissions from '../../hooks/usePermissions'
import SalesOrderPopup from '../modal/SalesOrderPopup'
import Loader from '../common/GlobalLoader'

function formatMoney(value) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value)
}

const OrderCard = () => {
  const storedUser = localStorage.getItem('user')
  const CUSTOMER_ID = storedUser ? JSON.parse(storedUser).CustomerID : null

  const [page, setPage] = useState(1)
  const [favourites, setFavourites] = useState([])
  const [showFavourites, setShowFavourites] = useState(false)
  const [selectedOrderId, setSelectedOrderId] = useState(null)
  const [popupOpen, setPopupOpen] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const pageSize = 5

  const { salesOrders, loading, error, orderDetails, detailLoading, detailError } =
    usePersonalOrdersByCustomerId(CUSTOMER_ID, selectedOrderId)
  const { canViewPrices } = usePermissions()

  let filteredOrders = salesOrders || []

  // ✅ Date Range Filter
  if (startDate && endDate) {
    filteredOrders = filteredOrders.filter(order => {
      const orderDate = new Date(order.date)
      return orderDate >= new Date(startDate) && orderDate <= new Date(endDate)
    })
  } else if (startDate) {
    filteredOrders = filteredOrders.filter(order => new Date(order.date) >= new Date(startDate))
  } else if (endDate) {
    filteredOrders = filteredOrders.filter(order => new Date(order.date) <= new Date(endDate))
  }

  const allOrders = showFavourites
    ? filteredOrders.filter(order => favourites.includes(order.salesorder_id))
    : filteredOrders

  const totalPages = Math.ceil(allOrders.length / pageSize)
  const dataToShow = allOrders.slice((page - 1) * pageSize, page * pageSize)

  const toggleFavourite = (id) => {
    setFavourites(prev =>
      prev.includes(id) ? prev.filter(favId => favId !== id) : [...prev, id]
    )
  }

  return (
    <div className="bg-white border rounded-xl overflow-hidden">
      {/* HEADER */}
      <div className="p-4 md:p-6 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div>
          <h2 className="text-lg md:text-2xl font-semibold theme-text-primary">
            Sales Orders <span className="text-gray-500 font-normal">(Recent Orders)</span>
          </h2>
          <div className="mt-1 md:mt-2 text-sm text-gray-600">
            Displaying{' '}
            <span className="font-medium">
              {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, allOrders.length)}
            </span>{' '}
            (of <span className="font-medium">{allOrders.length}</span> orders)
          </div>
        </div>

        {/* ✅ Date Range Filter UI */}
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto min-w-0">
            <input
              type="date"
              value={startDate}
              onChange={e => { setStartDate(e.target.value); setPage(1); }}
              className="px-2 py-2 rounded-md border text-sm w-full min-w-0 sm:w-auto sm:min-w-[9.5rem]"
            />
            <span className="text-gray-500 text-sm text-center sm:text-left hidden sm:inline shrink-0" aria-hidden="true">to</span>
            <input
              type="date"
              value={endDate}
              onChange={e => { setEndDate(e.target.value); setPage(1); }}
              className="px-2 py-2 rounded-md border text-sm w-full min-w-0 sm:w-auto sm:min-w-[9.5rem]"
            />
          </div>
          <button
            onClick={() => setShowFavourites(prev => !prev)}
            className="px-3 py-2 rounded-md border hover:bg-gray-50 text-sm font-medium w-full sm:w-auto shrink-0"
          >
            {showFavourites ? 'Show All' : 'Show Favourites'}
          </button>
        </div>
      </div>

      {/* TABLE HEADER */}
      <div className="hidden md:block border-t text-sm font-medium text-gray-600 overflow-x-auto">
        <div className={`min-w-[900px] grid gap-2 px-4 md:px-6 py-3 ${canViewPrices ? 'grid-cols-[60px_1fr_120px_120px_120px_120px_80px]' : 'grid-cols-[60px_1fr_120px_120px_120px_80px]'}`}>
          <div>Fav</div>
          <div>Order #</div>
          <div>Customer</div>
          <div>Date</div>
          <div>Status</div>
          {canViewPrices && <div className="text-right">Total</div>}
          <div className="text-center">View</div>
        </div>
      </div>

      {/* TABLE BODY */}
      <div className="divide-y">
        {loading && <Loader />}
        {error && <div className="px-4 py-6 text-center text-red-500">{error}</div>}
        {!loading && !error && dataToShow.length === 0 && (
          <div className="px-4 py-6 text-center text-gray-500">No sales orders found.</div>
        )}

        {dataToShow.map(order => (
          <div
            key={order.salesorder_id}
            className={`grid gap-2 items-center px-4 md:px-6 py-3 text-sm hover:bg-gray-50 ${canViewPrices ? 'md:grid-cols-[60px_1fr_120px_120px_120px_120px_80px]' : 'md:grid-cols-[60px_1fr_120px_120px_120px_80px]'}`}
          >
            {/* Mobile layout */}
            <div className="md:hidden flex flex-col gap-2 border rounded-lg p-3 bg-gray-50">
              <div className="flex justify-between items-center">
                <button onClick={() => toggleFavourite(order.salesorder_id)}>
                  <FaHeart className={favourites.includes(order.salesorder_id) ? 'text-red-500' : 'text-gray-300'} />
                </button>
                <div
                  className="text-blue-700 underline cursor-pointer"
                  onClick={() => { setSelectedOrderId(order.salesorder_id); setPopupOpen(true); }}
                >
                  #{order.salesorder_number}
                </div>
              </div>
              <div className="text-gray-700 text-sm"><b>Customer:</b> {order.customer_name}</div>
              <div className="text-gray-700 text-sm"><b>Date:</b> {order.date}</div>
              <div className="text-gray-700 text-sm"><b>Status:</b> {order.status}</div>
              {canViewPrices && (
                <div className="text-gray-700 text-sm"><b>Total:</b> {formatMoney(order.total)}</div>
              )}
              <button
                className="mt-2 px-3 py-1 rounded border text-sm hover:bg-gray-100"
                onClick={() => { setSelectedOrderId(order.salesorder_id); setPopupOpen(true); }}
              >
                View
              </button>
            </div>

            {/* Desktop layout */}
            <div className="hidden md:flex items-center justify-center">
              <button onClick={() => toggleFavourite(order.salesorder_id)}>
                <FaHeart className={favourites.includes(order.salesorder_id) ? 'text-red-500' : 'text-gray-300'} />
              </button>
            </div>
            <div
              className="hidden md:block text-blue-700 underline cursor-pointer"
              onClick={() => { setSelectedOrderId(order.salesorder_id); setPopupOpen(true); }}
            >
              {order.salesorder_number}
            </div>
            <div className="hidden md:block">{order.customer_name}</div>
            <div className="hidden md:block">{order.date}</div>
            <div className="hidden md:block">{order.status}</div>
            {canViewPrices && (
              <div className="hidden md:block text-right">{formatMoney(order.total)}</div>
            )}
            <div className="hidden md:block text-center">
              <button
                className="px-2 py-1 rounded border hover:bg-gray-100"
                onClick={() => { setSelectedOrderId(order.salesorder_id); setPopupOpen(true); }}
              >
                View
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION */}
      <div className="px-4 md:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-700 border-t">
        <button
          onClick={() => setPage(page - 1)}
          className="px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
          disabled={page === 1}
        >
          &laquo; Back
        </button>

        <div className="flex flex-wrap items-center justify-center gap-1">
          {(() => {
            let start = 1, end = totalPages
            if (totalPages > 5) {
              if (page <= 3) { start = 1; end = 5 }
              else if (page >= totalPages - 2) { start = totalPages - 4; end = totalPages }
              else { start = page - 2; end = page + 2 }
            }
            const buttons = []
            for (let i = start; i <= end; i++) {
              buttons.push(
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`px-2 py-1 rounded border ${page === i ? "bg-gray-200 font-medium" : "hover:bg-gray-50"}`}
                >
                  {i}
                </button>
              )
            }
            if (totalPages > 5 && end < totalPages) {
              buttons.push(<span key="ellipsis" className="px-1">...</span>)
            }
            return buttons
          })()}
        </div>

        <button
          onClick={() => setPage(page + 1)}
          className="px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
          disabled={page === totalPages || totalPages === 0}
        >
          Next &raquo;
        </button>
      </div>

      {/* POPUP */}
      <SalesOrderPopup
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        title={orderDetails ? `Sales Order #${orderDetails.salesorder.salesorder_number}` : 'Sales Order Details'}
        items={[]}
      >
        {detailLoading && <Loader />}
        {detailError && <div className="py-6 text-center text-red-500">{detailError}</div>}

        {orderDetails ? (
          <div>
            <div className="mb-4 text-sm">
              <div><b>Customer:</b> {orderDetails.salesorder.customer_name}</div>
              <div><b>Date:</b> {orderDetails.salesorder.date}</div>
              <div><b>Status:</b> {orderDetails.salesorder.status}</div>
              {canViewPrices && (
                <div><b>Total:</b> {formatMoney(orderDetails.salesorder.total)}</div>
              )}
              <div><b>Order Number:</b> {orderDetails.salesorder.salesorder_number}</div>
            </div>

            <div>
              <b>Items:</b>
              <div className="mt-2 border rounded overflow-x-auto">
                <div className={`min-w-[400px] grid gap-2 px-2 py-2 font-semibold bg-gray-50 ${canViewPrices ? 'grid-cols-[1fr_80px_80px_100px]' : 'grid-cols-[1fr_80px]'}`}>
                  <div>Item</div>
                  <div>Qty</div>
                  {canViewPrices && (
                    <>
                      <div>Rate</div>
                      <div className="text-right">Amount</div>
                    </>
                  )}
                </div>
                {orderDetails.salesorder.line_items?.map((item, idx) => (
                  <div key={item.item_id || idx} className={`min-w-[400px] grid gap-2 px-2 py-2 border-t text-sm ${canViewPrices ? 'grid-cols-[1fr_80px_80px_100px]' : 'grid-cols-[1fr_80px]'}`}>
                    <div>{item.name}</div>
                    <div>{item.quantity}</div>
                    {canViewPrices && (
                      <>
                        <div>{formatMoney(item.rate)}</div>
                        <div className="text-right">{formatMoney(item.item_sub_total)}</div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : !detailLoading && (
          <div className="py-6 text-center text-gray-500">No details found.</div>
        )}
      </SalesOrderPopup>
    </div>
  )
}

export default OrderCard
